import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@clerk/react";
import { GraduationCap, Plus, Trash2, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import ShareButton from "@/components/ShareButton";
import TeacherQuizzes from "@/components/teach/TeacherQuizzes";
import { makeApi, publicApi } from "@/lib/api";

const STATUS_BG = { approved: "bg-[var(--tp-success)] text-white", rejected: "bg-[#FF4B4B] text-white", pending: "bg-[var(--tp-accent)]" };

function QuizPicker({ api, room, onSaved }) {
  const [all, setAll] = useState([]);
  const [sel, setSel] = useState(new Set(room.quiz_ids || []));
  useEffect(() => {
    publicApi.get("/categories").then(async (r) => {
      const lists = await Promise.all(r.data.map((c) => publicApi.get(`/categories/${c.id}/quizzes`).then((x) => x.data.map((q) => ({ ...q, cat: c.name })))));
      const own = await api.get("/teacher/quizzes").then((x) => x.data.filter((q) => q.visibility === "class").map((q) => ({ ...q, cat: "My class quiz" })));
      setAll([...own, ...lists.flat()]);
    }).catch((e) => console.error("Failed to load quizzes", e));
  }, [api]);
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const save = async () => { await api.put(`/classrooms/${room.id}/quizzes`, { quiz_ids: [...sel] }); toast.success("Assignments saved"); onSaved(); };
  return (
    <div className="sticker-card p-5">
      <h3 className="font-black text-lg mb-3">Assign quizzes</h3>
      <div className="max-h-64 overflow-auto space-y-1">
        {all.map((q) => (
          <label key={q.id} className="flex items-center gap-2 font-bold">
            <input type="checkbox" checked={sel.has(q.id)} onChange={() => toggle(q.id)} className="w-5 h-5" data-testid={`assign-${q.id}`} />
            {q.title} <span className="text-slate-400 text-sm">({q.cat})</span>
          </label>
        ))}
      </div>
      <button onClick={save} data-testid="save-assignments"
        className="mt-4 px-5 py-2 rounded-full border-[3px] border-[#374151] bg-[var(--tp-primary)] text-white font-black shadow-[0_3px_0_0_#374151]">Save</button>
    </div>
  );
}

function RoomDetail({ api, id, onBack }) {
  const [room, setRoom] = useState(null);
  const load = useCallback(() => api.get(`/classrooms/${id}`).then((r) => setRoom(r.data)), [api, id]);
  useEffect(() => { load(); }, [load]);
  if (!room) return <p>Loading…</p>;
  const remove = async (uid) => { if (!window.confirm("Remove student?")) return; await api.delete(`/classrooms/${id}/members/${uid}`); load(); };

  return (
    <div data-testid="classroom-detail">
      <button onClick={onBack} className="inline-flex items-center gap-1 font-bold mb-4 hover:underline" data-testid="back-to-classes"><ChevronLeft size={18} /> All classrooms</button>
      <div className="flex items-center gap-3 flex-wrap">
        <h2 className="text-3xl font-black">{room.name}</h2>
        <span className={`px-3 py-1 rounded-full border-[2px] border-[#374151] font-black text-xs uppercase ${STATUS_BG[room.status]}`}>{room.status}</span>
      </div>
      {room.status === "approved" ? (
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <span className="font-bold">Class code:</span>
          <span className="px-4 py-2 rounded-xl border-[3px] border-[#374151] bg-[#FFF4C8] font-black tracking-widest text-xl" data-testid="class-code">{room.code}</span>
          <ShareButton path={`/classes?code=${room.code}`} label="Share invite link" testid="share-class-invite" title={`Join ${room.name} on TP`} />
        </div>
      ) : <p className="mt-2 text-slate-600 font-bold">Your class code becomes active once an admin approves this classroom.</p>}

      <div className="grid lg:grid-cols-2 gap-6 mt-8">
        <QuizPicker api={api} room={room} onSaved={load} />
        <div className="sticker-card p-5">
          <h3 className="font-black text-lg mb-3">Assigned quiz links</h3>
          {room.quizzes.length === 0 && <p className="text-slate-500">None yet.</p>}
          {room.quizzes.map((q) => (
            <div key={q.id} className="flex items-center justify-between gap-2 py-2 border-b-[2px] border-slate-100 last:border-0">
              <span className="font-bold">{q.title}</span>
              <ShareButton path={`/quiz/${q.id}`} label="Copy link" testid={`share-assigned-${q.id}`} title={q.title} />
            </div>
          ))}
        </div>
      </div>

      <h3 className="text-2xl font-black mt-10 mb-3">Students ({room.students.length})</h3>
      <div className="sticker overflow-x-auto" data-testid="students-table">
        {room.students.length === 0 && <p className="p-5 text-slate-500">No students yet — share the class code!</p>}
        {room.students.map((s) => (
          <div key={s.user_id} className="p-4 border-b-[2px] border-slate-200 last:border-0">
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="font-black truncate">{s.name || s.email}</p>
                <p className="text-sm text-slate-500">{s.plays} play(s){s.avg_pct !== null ? ` • avg ${s.avg_pct}%` : ""}</p>
              </div>
              <button onClick={() => remove(s.user_id)} className="text-[#FF4B4B]" data-testid={`remove-student-${s.user_id}`}><Trash2 size={16} /></button>
            </div>
            {s.best.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {s.best.map((b) => (
                  <span key={b.quiz_title} className="text-xs font-bold px-2 py-1 rounded-full border-[2px] border-[#374151] bg-[#EBFBD6]">{b.quiz_title}: {b.score}/{b.total}</span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Teach() {
  const { getToken } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const [me, setMe] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [form, setForm] = useState({ name: "", description: "" });
  const [openId, setOpenId] = useState(null);
  const [tab, setTab] = useState("classrooms");
  const load = useCallback(() => api.get("/classrooms/teaching").then((r) => setRooms(r.data)), [api]);
  useEffect(() => { api.get("/me").then((r) => { setMe(r.data); if (["teacher", "admin"].includes(r.data.role)) load(); }); }, [api, load]);

  if (!me) return <><Nav /><p className="p-8">Loading…</p></>;
  if (!["teacher", "admin"].includes(me.role)) {
    return <><Nav /><main className="max-w-xl mx-auto p-10 text-center" data-testid="teach-locked">
      <GraduationCap size={48} strokeWidth={2.5} className="mx-auto text-[var(--tp-primary)]" />
      <h1 className="mt-4 text-3xl font-black">Instructor Portal</h1>
      <p className="text-slate-600 mt-2">Only verified teachers can create classrooms. Request access from your profile page.</p>
      <a href="/profile" className="mt-6 inline-block px-6 py-3 rounded-full border-[3px] border-[#374151] bg-[var(--tp-primary)] text-white font-black shadow-[0_4px_0_0_#374151]">Go to profile</a>
    </main></>;
  }

  const create = async () => {
    if (!form.name.trim()) return toast.error("Name your classroom");
    const r = await api.post("/classrooms", form);
    toast.success(r.data.status === "approved" ? "Classroom created" : "Classroom submitted for approval");
    setForm({ name: "", description: "" }); load();
  };
  const del = async (id) => { if (!window.confirm("Delete classroom?")) return; await api.delete(`/classrooms/${id}`); load(); };

  return (
    <>
      <Nav />
      <main className="max-w-5xl mx-auto px-4 py-10">
        <h1 className="text-4xl font-black tracking-tight flex items-center gap-2 mb-6"><GraduationCap size={32} strokeWidth={2.5} /> Instructor Portal</h1>
        <div className="flex gap-2 mb-8">
          {[["classrooms", "Classrooms"], ["quizzes", "My class quizzes"]].map(([k, label]) => (
            <button key={k} onClick={() => { setTab(k); setOpenId(null); }} data-testid={`teach-tab-${k}`}
              className={`px-4 py-2 rounded-full border-[3px] border-[#374151] font-black text-sm shadow-[0_3px_0_0_#374151] ${tab === k ? "bg-[var(--tp-accent)]" : "bg-white"}`}>{label}</button>
          ))}
        </div>
        {tab === "quizzes" ? <TeacherQuizzes api={api} /> : openId ? <RoomDetail api={api} id={openId} onBack={() => { setOpenId(null); load(); }} /> : (
          <>
            <div className="sticker-card p-6" style={{ background: "#E6F7FF" }}>
              <h2 className="font-black text-lg mb-3">New classroom</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. 5th Grade Science" data-testid="classroom-name"
                  className="px-4 py-3 rounded-xl border-[3px] border-[#374151] font-bold bg-white" />
                <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Short description"
                  className="px-4 py-3 rounded-xl border-[3px] border-[#374151] font-bold bg-white" />
              </div>
              <button onClick={create} data-testid="create-classroom"
                className="mt-4 px-5 py-2.5 rounded-full border-[3px] border-[#374151] bg-[var(--tp-success)] text-white font-black shadow-[0_4px_0_0_#374151] inline-flex items-center gap-1">
                <Plus size={16} strokeWidth={2.5} /> Create
              </button>
              <p className="text-sm text-slate-600 mt-2">New classrooms are reviewed by an admin before students can join.</p>
            </div>
            <div className="mt-8 grid md:grid-cols-2 gap-4" data-testid="teaching-list">
              {rooms.length === 0 && <p className="text-slate-500">No classrooms yet.</p>}
              {rooms.map((c) => (
                <div key={c.id} className="sticker-card p-5">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-xl font-black">{c.name}</h3>
                    <span className={`px-3 py-1 rounded-full border-[2px] border-[#374151] font-black text-xs uppercase ${STATUS_BG[c.status]}`}>{c.status}</span>
                  </div>
                  <p className="text-slate-600 text-sm mt-1">{c.member_ids?.length || 0} students • {c.quiz_ids?.length || 0} quizzes</p>
                  <div className="mt-4 flex gap-2">
                    <button onClick={() => setOpenId(c.id)} data-testid={`open-classroom-${c.id}`}
                      className="px-4 py-2 rounded-full border-[3px] border-[#374151] bg-[var(--tp-primary)] text-white font-black text-sm shadow-[0_3px_0_0_#374151]">Open</button>
                    <button onClick={() => del(c.id)} className="px-3 py-2 text-[#FF4B4B]" data-testid={`delete-classroom-${c.id}`}><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </>
  );
}
